/**
 * App.tsx - Routing with Landing Page + /app/* Split
 *
 * Structure:
 * /           → Landing (public)
 * /app/*      → Product (auth-gated)
 * /v/:token   → Verification (public)
 * /terms      → Terms (public)
 * /privacy    → Privacy (public)
 */

import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Canvas Navigation (Legacy)
import { CanvasLayout } from './components/navigation';

// New Navigation (Design System Migration Phase 2)
import { AppLayout } from './components/layout/AppLayout';
import { useFeatureFlag } from './hooks/useFeatureFlag';

// Hooks
import { useReducedMotion } from './hooks/useReducedMotion';
import { useAuthReady } from './hooks/useAuthReady';

// Brand Components
import { SplashScreen } from './components/brand';
import { T } from './styles/tokens';

// Paywall
import { PaywallProvider, usePaywall } from './hooks';
import { PaywallModal } from './components/PaywallModal';

// Plaid Link (singleton)
import { PlaidLinkProvider } from './contexts/PlaidLinkContext';

// ============================================
// CRITICAL PATH PAGES (Eager Load)
// These must load immediately for best UX
// ============================================
import Landing from './pages/Landing';
import { CalculatorPage } from './pages/CalculatorPage';
import { ResultPage } from './pages/ResultPage';
import { ResultPageMinimal } from './pages/ResultPageMinimal';
import { LoginPage } from './pages/LoginPage';
import { LoginEmailPage } from './pages/LoginEmailPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import {ProfileSetupPage} from './pages/ProfileSetupPage';
import OnboardingV3 from './pages/OnboardingV3';
import OnboardingV31 from './pages/OnboardingV31';
import UnifiedOnboarding from './pages/UnifiedOnboarding';
import { StudioPage } from './pages/StudioPage';
import { StudioTab } from './components/Studio';
import { PortfolioPage } from './pages/PortfolioPage';
import { DashboardPage } from './pages/MoneyTabPage';
import NotFound from './pages/NotFound';
import { InstallBanner } from './components/pwa/InstallBanner';

// ============================================
// NON-CRITICAL PAGES (Lazy Load)
// These are loaded on-demand to reduce initial bundle
// ============================================
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage').then(m => ({ default: m.SubscriptionSuccessPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage').then(m => ({ default: m.HowItWorksPage })));
const VerdictDetailPage = lazy(() => import('./pages/VerdictDetailPage').then(m => ({ default: m.VerdictDetailPage })));
const PremiumPage = lazy(() => import('./pages/PremiumPage').then(m => ({ default: m.PremiumPage })));

const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const InsightsPage = lazy(() => import('./pages/InsightsPage').then(m => ({ default: m.InsightsPage })));
const ExportPage = lazy(() => import('./pages/ExportPage').then(m => ({ default: m.ExportPage })));
const LogSaleSelectionPage = lazy(() => import('./pages/LogSaleSelectionPage').then(m => ({ default: m.LogSaleSelectionPage })));
const PriceSavedPage = lazy(() => import('./pages/PriceSavedPage').then(m => ({ default: m.PriceSavedPage })));
const PriceAnatomyPage = lazy(() => import('./pages/PriceAnatomyPage').then(m => ({ default: m.PriceAnatomyPage })));
const SubscribePage = lazy(() => import('./pages/SubscribePage').then(m => ({ default: m.SubscribePage })));
const BillingSuccessPage = lazy(() => import('./pages/BillingSuccessPage').then(m => ({ default: m.BillingSuccessPage })));
const BillingCanceledPage = lazy(() => import('./pages/BillingCanceledPage').then(m => ({ default: m.BillingCanceledPage })));
const AnalysisPage = lazy(() => import('./pages/AnalysisPage').then(m => ({ default: m.AnalysisPage })));
const EditStudioPage = lazy(() => import('./pages/EditStudioPage').then(m => ({ default: m.EditStudioPage })));
const TextureDemoPage = lazy(() => import('./pages/TextureDemoPage').then(m => ({ default: m.TextureDemoPage })));
// RevealPage removed — burn reveal screen deleted
const ZoneDrilldownPage = lazy(() => import('./pages/ZoneDrilldownPage'));
const AccountDetailPage = lazy(() => import('./pages/AccountDetailPage').then(m => ({ default: m.AccountDetailPage })));

import { DelayedFallback } from '@/components/ui/DelayedFallback';

// Auth
import {
  ProtectedRoute,
  RequireOnboarding,
  RedirectIfAuthenticated,
  BlockIfOnboardingComplete,
} from './components/auth';

// Horizontal slide animation
const slideVariants = {
  enterFromLeft: { x: '-100%', y: 0 },
  enterFromRight: { x: '100%', y: 0 },
  enterFromBottom: { x: 0, y: '100%' },
  center: { x: 0, y: 0 },
  exitToLeft: { x: '-100%', y: 0 },
  exitToRight: { x: '100%', y: 0 },
  exitToBottom: { x: 0, y: '100%' },
};

const slideTransition = {
  type: 'tween' as const,
  duration: 0.28,
  ease: [0.32, 0.72, 0, 1] as const,
};

const slideUpTransition = {
  type: 'tween' as const,
  duration: 0.38,
  ease: [0.32, 0.72, 0, 1] as const,
};

// Route categories for /app/* paths
const SLIDE_LEFT_PAGES = ['/app/calculate', '/app/result', '/app/price-saved', '/app/subscribe', '/app/studio/edit', '/app/terms', '/app/privacy', '/app/how-it-works'];
const SLIDE_RIGHT_PAGES = ['/app/log-sale'];
const CANVAS_PAGES = ['/app', '/app/dashboard', '/app/portfolio', '/app/insights'];

function isSlideUpPath(path: string): boolean {
  return path.startsWith('/app/verdict/') || path.startsWith('/app/adjust/') || path.startsWith('/app/analysis/') || path.startsWith('/app/zone/') || path.startsWith('/app/account/');
}

function shouldShowCanvas(path: string): boolean {
  const isCanvasPage = CANVAS_PAGES.includes(path);
  const isSlideOverPage = SLIDE_LEFT_PAGES.includes(path) ||
                          SLIDE_RIGHT_PAGES.includes(path) ||
                          isSlideUpPath(path);
  return isCanvasPage || isSlideOverPage;
}

/**
 * AuthGatedLanding - Root route handler
 *
 * Shows branded landing page for unauthenticated users
 * Redirects authenticated users to dashboard
 */
function AuthGatedLanding() {
  const { isReady, isAuthenticated } = useAuthReady();

  // Still loading auth - splash handles this, but safety check
  if (!isReady) return null;

  // Authenticated user → redirect to dashboard
  if (isAuthenticated) return <Navigate to="/app/dashboard" replace />;

  // Unauthenticated → show marketing page
  return <Landing />;
}

function App() {
  const location = useLocation();
  const currentPath = location.pathname;
  const useNewNavigation = useFeatureFlag('NEW_NAVIGATION');
  const useMinimalResult = useFeatureFlag('MINIMAL_RESULT_SCREEN');

  // Auth readiness (splash handled by AppWithPaywall overlay)
  const { isReady: authReady } = useAuthReady();

  // Check if we're on a public page (not /app/*)
  const isPublicPage = currentPath === '/' ||
                       currentPath === '/terms' ||
                       currentPath === '/privacy' ||
                       currentPath === '/how-it-works' ||
                       currentPath === '/login' ||
                       currentPath === '/login/email' ||
                       currentPath === '/signup' ||
                       currentPath === '/forgot-password' ||
                       currentPath.startsWith('/onboarding') ||
                       currentPath === '/billing/success' ||
                       currentPath === '/billing/canceled' ||
                       currentPath === '/subscription-success' ||
                       currentPath === '/texture-demo';

  // Check if we're on the app pages
  const isAppPage = currentPath.startsWith('/app');

  const isSlideLeftPage = SLIDE_LEFT_PAGES.includes(currentPath);
  const isSlideRightPage = SLIDE_RIGHT_PAGES.includes(currentPath);
  const isSlideUpPage = isSlideUpPath(currentPath);
  const isSlidePage = isSlideLeftPage || isSlideRightPage || isSlideUpPage;
  const showCanvas = shouldShowCanvas(currentPath);

  const slideDirection = isSlideLeftPage ? 'left' : isSlideRightPage ? 'right' : 'up';

  // ========================================
  // PUBLIC PAGES (no auth required)
  // ========================================
  if (isPublicPage) {
    return (
      <div className="min-h-screen bg-cream-100">
        <Suspense fallback={<DelayedFallback />}>
          <Routes>
            {/* Landing */}
            <Route path="/" element={<AuthGatedLanding />} />

            {/* Legal */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />

            {/* Auth */}
            <Route path="/login" element={
              <RedirectIfAuthenticated><LoginPage /></RedirectIfAuthenticated>
            } />
            <Route path="/login/email" element={
              <RedirectIfAuthenticated><LoginEmailPage /></RedirectIfAuthenticated>
            } />
            <Route path="/signup" element={
              <RedirectIfAuthenticated><SignupPage /></RedirectIfAuthenticated>
            } />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/onboarding/*" element={
              <BlockIfOnboardingComplete><UnifiedOnboarding /></BlockIfOnboardingComplete>
            } />

            {/* Billing callbacks */}
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/canceled" element={<BillingCanceledPage />} />
            <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />

            {/* Onboarding V3.1 (test route) */}
            <Route path="/onboarding-v3" element={<OnboardingV31 />} />

            {/* Dev/Demo */}
            <Route path="/texture-demo" element={<TextureDemoPage />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  // ========================================
  // LEGACY REDIRECTS (old URLs → /app/*)
  // ========================================
  const legacyRedirects = (
    <Routes>
      <Route path="/portfolio" element={<Navigate to="/app/portfolio" replace />} />
      <Route path="/calculate" element={<Navigate to="/app/calculate" replace />} />
      <Route path="/studio" element={<Navigate to="/app/insights" replace />} />
      <Route path="/studio/edit" element={<Navigate to="/app/studio/edit" replace />} />
      <Route path="/settings" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/verdict/:id" element={<Navigate to="/app/verdict/:id" replace />} />
      <Route path="/result" element={<Navigate to="/app/result" replace />} />
      <Route path="/price-saved" element={<Navigate to="/app/price-saved" replace />} />
      <Route path="/new-price" element={<Navigate to="/app/calculate" replace />} />
      <Route path="/new-price-result" element={<Navigate to="/app/result" replace />} />
      <Route path="/subscribe" element={<Navigate to="/app/subscribe" replace />} />
      <Route path="/log-sale" element={<Navigate to="/app/log-sale" replace />} />
      <Route path="/adjust/:id" element={<Navigate to="/app/adjust/:id" replace />} />
      <Route path="/analysis/:id" element={<Navigate to="/app/analysis/:id" replace />} />
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/pipeline" element={<Navigate to="/app/portfolio" replace />} />
      <Route path="/welcome" element={<Navigate to="/" replace />} />
      <Route path="/auth" element={<Navigate to="/signup" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  // Handle legacy URLs
  if (!isAppPage && !isPublicPage) {
    return legacyRedirects;
  }

  // ========================================
  // NEW NAVIGATION (Design System Migration)
  // ========================================
  if (useNewNavigation) {
    return (
      <div className="min-h-screen bg-cream-100">
        <InstallBanner />
        <RequireOnboarding>
          <AppLayout>
            <Suspense fallback={<DelayedFallback />}>
              <Routes>
                {/* Capacitor native opens /app.html directly — route to dashboard */}
                <Route path="/app.html" element={<Navigate to="/app/dashboard" replace />} />
                <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
                <Route path="/app/dashboard" element={<DashboardPage />} />
                <Route path="/app/portfolio" element={<PortfolioPage />} />
                <Route path="/app/insights" element={<InsightsPage />} />
                <Route path="/app/calculate" element={<CalculatorPage />} />
                <Route path="/app/result" element={useMinimalResult ? <ResultPageMinimal /> : <ResultPage />} />
                <Route path="/app/price-saved" element={<PriceSavedPage />} />
                <Route path="/app/adjust/:id" element={<PriceAnatomyPage />} />
                <Route path="/app/new-price" element={<Navigate to="/app/calculate" replace />} />
                <Route path="/app/new-price-result" element={<Navigate to="/app/result" replace />} />
                <Route path="/app/studio" element={<Navigate to="/app/insights" replace />} />
                <Route path="/app/studio/edit" element={<EditStudioPage />} />
                <Route path="/app/premium" element={<PremiumPage />} />
                <Route path="/app/settings" element={<SettingsPage />} />
                <Route path="/app/notifications" element={<NotificationsPage />} />
                <Route path="/app/export" element={<ExportPage />} />
                <Route path="/app/terms" element={<TermsPage />} />
                <Route path="/app/privacy" element={<PrivacyPage />} />
                <Route path="/app/how-it-works" element={<HowItWorksPage />} />
                <Route path="/app/verdict/:id" element={<VerdictDetailPage />} />
                <Route path="/app/log-sale" element={<LogSaleSelectionPage />} />
                <Route path="/app/subscribe" element={<SubscribePage />} />
                <Route path="/app/analysis/:id" element={<AnalysisPage />} />
                <Route path="/app/zone/:category" element={<ZoneDrilldownPage />} />
                <Route path="/app/account/:accountId" element={<AccountDetailPage />} />
                <Route path="/app/profile-setup" element={
                  <ProtectedRoute><ProfileSetupPage /></ProtectedRoute>
                } />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AppLayout>
        </RequireOnboarding>
      </div>
    );
  }

  // ========================================
  // LEGACY CANVAS NAVIGATION
  // ========================================

  useEffect(() => {
    if (CANVAS_PAGES.includes(currentPath)) {
      sessionStorage.setItem('lastCanvasTab', currentPath);
    }
  }, [currentPath]);

  return (
    <div className="min-h-screen bg-cream-100 relative overflow-hidden">
      <InstallBanner />
      {/* Canvas Layout */}
      {showCanvas && (
        <RequireOnboarding>
          <div className="fixed inset-0" style={{ zIndex: 1 }}>
            <CanvasAppShell currentPath={currentPath} />
          </div>
        </RequireOnboarding>
      )}

      {/* Slide-over pages */}
      <AnimatePresence>
        {isSlidePage && (
          <RequireOnboarding>
            <SlideOverPage
              key={currentPath}
              direction={slideDirection}
            >
              <Suspense fallback={<DelayedFallback />}>
                <Routes location={location}>
                  <Route path="/app/calculate" element={<CalculatorPage />} />
                  <Route path="/app/result" element={useMinimalResult ? <ResultPageMinimal /> : <ResultPage />} />
                  <Route path="/app/price-saved" element={<PriceSavedPage />} />
                  <Route path="/app/adjust/:id" element={<PriceAnatomyPage />} />
                  <Route path="/app/new-price" element={<Navigate to="/app/calculate" replace />} />
                  <Route path="/app/new-price-result" element={<Navigate to="/app/result" replace />} />
                  <Route path="/app/log-sale" element={<LogSaleSelectionPage />} />
                  <Route path="/app/subscribe" element={<SubscribePage />} />
                  <Route path="/app/verdict/:id" element={<VerdictDetailPage />} />
                  <Route path="/app/studio/edit" element={<EditStudioPage />} />
                  <Route path="/app/analysis/:id" element={<AnalysisPage />} />
                    <Route path="/app/zone/:category" element={<ZoneDrilldownPage />} />
                  <Route path="/app/account/:accountId" element={<AccountDetailPage />} />
                  <Route path="/app/terms" element={<TermsPage />} />
                  <Route path="/app/privacy" element={<PrivacyPage />} />
                  <Route path="/app/how-it-works" element={<HowItWorksPage />} />
                </Routes>
              </Suspense>
            </SlideOverPage>
          </RequireOnboarding>
        )}
      </AnimatePresence>

      {/* Route placeholders for canvas pages */}
      <Routes>
        <Route path="/app" element={null} />
        <Route path="/app/dashboard" element={null} />
        <Route path="/app/portfolio" element={null} />
        <Route path="/app/insights" element={null} />
        <Route path="/app/studio" element={<Navigate to="/app/insights" replace />} />
        <Route path="/app/studio/edit" element={null} />
        <Route path="/app/calculate" element={null} />
        <Route path="/app/result" element={null} />
        <Route path="/app/price-saved" element={null} />
        <Route path="/app/adjust/:id" element={null} />
        <Route path="/app/log-sale" element={null} />
        <Route path="/app/subscribe" element={null} />
        <Route path="/app/verdict/:id" element={null} />
        <Route path="/app/terms" element={null} />
        <Route path="/app/privacy" element={null} />
        <Route path="/app/how-it-works" element={null} />
        <Route path="/app/analysis/:id" element={null} />
        <Route path="/app/zone/:category" element={null} />
        <Route path="/app/account/:accountId" element={null} />
        <Route path="/app/profile-setup" element={
          <ProtectedRoute><ProfileSetupPage /></ProtectedRoute>
        } />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

// ============================================
// CANVAS APP SHELL
// ============================================

interface CanvasAppShellProps {
  currentPath: string;
}

function CanvasAppShell({ currentPath }: CanvasAppShellProps) {
  const navigate = useNavigate();

  const getPageIndex = (path: string): number => {
    if (path === '/app/portfolio') return 1;
    if (path === '/app/insights') return 2;
    return 0; // /app or /app/dashboard
  };

  const [currentPage, setCurrentPage] = useState(getPageIndex(currentPath));

  useEffect(() => {
    if (CANVAS_PAGES.includes(currentPath)) {
      setCurrentPage(getPageIndex(currentPath));
    }
  }, [currentPath]);

  const handlePageChange = (_pageId: string, index: number) => {
    const paths = ['/app/dashboard', '/app/portfolio', '/app/insights'];
    const targetPath = paths[index];

    if (window.location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  };

  return (
    <CanvasLayout
      initialPage={currentPage}
      onPageChange={handlePageChange}
      dashboardContent={<DashboardPage />}
      portfolioContent={<PortfolioPage />}
      insightsContent={<InsightsPage />}
    />
  );
}

// ============================================
// SLIDE-OVER PAGE
// ============================================

interface SlideOverPageProps {
  children: React.ReactNode;
  direction: 'left' | 'right' | 'up';
}

function SlideOverPage({ children, direction }: SlideOverPageProps) {
  const prefersReducedMotion = useReducedMotion();

  const initial = direction === 'left'
    ? 'enterFromLeft'
    : direction === 'right'
    ? 'enterFromRight'
    : 'enterFromBottom';

  const exit = direction === 'left'
    ? 'exitToLeft'
    : direction === 'right'
    ? 'exitToRight'
    : 'exitToBottom';

  const transition = direction === 'up' ? slideUpTransition : slideTransition;

  return (
    <motion.div
      className="fixed inset-0 bg-cream-100 z-50"
      variants={slideVariants}
      initial={initial}
      animate="center"
      exit={exit}
      transition={prefersReducedMotion ? { duration: 0 } : transition}
    >
      <div className="h-full w-full overflow-y-auto overflow-x-hidden overscroll-y-contain">
        {children}
      </div>
    </motion.div>
  );
}

// Wrap App with providers + splash overlay
function AppWithPaywall() {
  const { isReady: authReady } = useAuthReady();
  const [splashAnimationDone, setSplashAnimationDone] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [splashFading, setSplashFading] = useState(false);

  // Dismiss splash when BOTH auth is ready AND animation is done
  const canDismiss = authReady && splashAnimationDone;

  useEffect(() => {
    if (canDismiss && !splashFading) {
      setSplashFading(true);
      const timer = setTimeout(() => setShowSplash(false), 400);
      return () => clearTimeout(timer);
    }
  }, [canDismiss, splashFading]);

  // Safety timeout - never show splash for more than 3s
  useEffect(() => {
    const safety = setTimeout(() => {
      if (showSplash) {
        setSplashFading(true);
        setTimeout(() => setShowSplash(false), 400);
      }
    }, 3000);
    return () => clearTimeout(safety);
  }, [showSplash]);

  return (
    <PaywallProvider>
      <PlaidLinkProvider>
        {/* App always renders — dashboard paints behind splash */}
        <App />
        <PaywallModalWrapper />

        {/* Splash overlay — fades out to reveal pre-rendered app */}
        {showSplash && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              opacity: splashFading ? 0 : 1,
              transition: `opacity 0.4s ${T.easeOut}`,
              pointerEvents: splashFading ? 'none' : 'auto',
            }}
          >
            <SplashScreen onReady={() => setSplashAnimationDone(true)} />
          </div>
        )}
      </PlaidLinkProvider>
    </PaywallProvider>
  );
}

// PaywallModal wrapper that uses the hook
function PaywallModalWrapper() {
  const { isPaywallOpen, closePaywall } = usePaywall();
  return <PaywallModal isOpen={isPaywallOpen} onClose={closePaywall} />;
}

export default AppWithPaywall;
